import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Users, User, ChevronDown, ChevronUp, X, Clock,
  Loader2, DollarSign, Receipt, Banknote, CreditCard, Smartphone, FileText as FileTextIcon, Landmark, Filter,
} from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { API, formatCurrency, MODE_COLORS } from "./paymentsConstants";

const STATUS_BADGE = {
  open: "bg-green-100 text-green-700",
  closed: "bg-slate-100 text-slate-600",
};

const PAY_BADGE = {
  PAID: "bg-emerald-100 text-emerald-700",
  PENDING: "bg-amber-100 text-amber-700",
  REFUNDED: "bg-blue-100 text-blue-700",
  CANCELLED: "bg-red-100 text-red-700",
};

const MODE_ICONS = {
  Cash: Banknote, Card: CreditCard, Credit: Receipt, UPI: Smartphone, EFT: Landmark, Others: FileTextIcon,
};
const MODE_STYLES = {
  Cash: { color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", ring: "ring-emerald-400" },
  Card: { color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", ring: "ring-blue-400" },
  Credit: { color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200", ring: "ring-indigo-400" },
  UPI: { color: "text-violet-700", bg: "bg-violet-50", border: "border-violet-200", ring: "ring-violet-400" },
  EFT: { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", ring: "ring-amber-400" },
  Others: { color: "text-red-700", bg: "bg-red-50", border: "border-red-200", ring: "ring-red-400" },
};
const MODES = ["Cash", "Card", "Credit", "EFT", "UPI", "Others"];

export function BillingCashierView({ dateFrom, dateTo }) {
  const [sessions, setSessions] = useState([]);
  const [cashierNames, setCashierNames] = useState([]);
  const [selectedCashier, setSelectedCashier] = useState("");
  const [loading, setLoading] = useState(false);
  const [namesLoading, setNamesLoading] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);
  const [sessionBills, setSessionBills] = useState({});
  const [billsLoading, setBillsLoading] = useState({});

  // Fetch all cashier names (independent of selection)
  useEffect(() => {
    (async () => {
      setNamesLoading(true);
      try {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const { data } = await axios.get(`${API}/api/payments/billing-cashier/sessions`, { params });
        setCashierNames(data.cashier_names || []);
      } catch (e) { console.error(e); }
      finally { setNamesLoading(false); }
    })();
  }, [dateFrom, dateTo]);

  // Fetch sessions only for the selected cashier
  const fetchSessions = useCallback(async () => {
    if (!selectedCashier) { setSessions([]); return; }
    setLoading(true);
    try {
      const params = { cashier_name: selectedCashier };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await axios.get(`${API}/api/payments/billing-cashier/sessions`, { params });
      setSessions(data.sessions || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, selectedCashier]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const toggleSession = useCallback(async (sessionId) => {
    if (expandedSession === sessionId) { setExpandedSession(null); return; }
    setExpandedSession(sessionId);
    if (sessionBills[sessionId]) return;
    setBillsLoading(prev => ({ ...prev, [sessionId]: true }));
    try {
      const { data } = await axios.get(`${API}/api/payments/billing-cashier/session/${sessionId}/bills`);
      setSessionBills(prev => ({ ...prev, [sessionId]: data }));
    } catch (e) { console.error(e); }
    finally { setBillsLoading(prev => ({ ...prev, [sessionId]: false })); }
  }, [expandedSession, sessionBills]);

  return (
    <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden" data-testid="billing-cashier-view">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-100"><Users className="w-4 h-4 text-indigo-600" /></div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">Billing Cashier Sessions</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {selectedCashier ? `${sessions.length} sessions for ${selectedCashier}` : "Select a cashier to view sessions"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedCashier}
            onChange={e => { setSelectedCashier(e.target.value); setExpandedSession(null); setSessionBills({}); }}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-200 min-w-[220px]"
            data-testid="billing-cashier-filter"
          >
            <option value="">-- Select Cashier --</option>
            {cashierNames.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {selectedCashier && (
            <button onClick={() => { setSelectedCashier(""); setExpandedSession(null); setSessionBills({}); setSessions([]); }}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600" data-testid="clear-cashier-filter">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        {!selectedCashier ? (
          <div className="text-center py-16 text-slate-400">
            <User className="w-10 h-10 mx-auto mb-2 text-slate-200" />
            <p className="text-sm font-medium">Select a cashier to view their billing sessions</p>
            <p className="text-xs mt-1">{namesLoading ? "Loading cashiers..." : `${cashierNames.length} cashiers available`}</p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading sessions...
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Users className="w-10 h-10 mx-auto mb-2 text-slate-200" />
            <p className="text-sm">No sessions found for {selectedCashier} in this period</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="w-8"></TableHead>
                <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">Session Start (IST)</TableHead>
                <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">Session End (IST)</TableHead>
                <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500 text-center">Status</TableHead>
                <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500 text-right">Opening Cash</TableHead>
                <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500 text-right">Ending Cash</TableHead>
                <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500 text-center">Bills</TableHead>
                <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500 text-right">Total Amount</TableHead>
                <TableHead className="font-semibold text-[10px] uppercase tracking-wider text-slate-500">Transfer To</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.map(s => {
                const isOpen = expandedSession === s.sessionId;
                const data = sessionBills[s.sessionId];
                const bLoading = billsLoading[s.sessionId];
                return [
                  <TableRow key={s.sessionId}
                    className={`cursor-pointer transition-colors ${isOpen ? "bg-indigo-50/40" : "hover:bg-slate-50/80"}`}
                    onClick={() => toggleSession(s.sessionId)}
                    data-testid={`session-row-${s.sessionId}`}>
                    <TableCell className="text-center">
                      {bLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500" /> :
                        isOpen ? <ChevronUp className="w-3.5 h-3.5 text-indigo-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      <div className="flex items-center gap-1.5"><Clock className="w-3 h-3 text-slate-400" />{s.startTime}</div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {s.endTime ? <div className="flex items-center gap-1.5"><Clock className="w-3 h-3 text-slate-400" />{s.endTime}</div> :
                        <span className="text-xs text-green-600 font-medium">In progress</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${STATUS_BADGE[s.status] || "bg-slate-100 text-slate-600"}`}>{s.status}</span>
                    </TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{formatCurrency(s.openingCash)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{formatCurrency(s.endingCash)}</TableCell>
                    <TableCell className="text-xs text-center font-semibold text-slate-700">{s.billCount}</TableCell>
                    <TableCell className="text-xs text-right font-semibold tabular-nums">{formatCurrency(s.totalAmount)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{s.transferCashierName || s.transferTo || "-"}</TableCell>
                  </TableRow>,
                  isOpen && (
                    <TableRow key={`bills-${s.sessionId}`} className="bg-slate-50/60">
                      <TableCell colSpan={9} className="p-0">
                        <SessionDrillDown data={data} isLoading={bLoading} />
                      </TableCell>
                    </TableRow>
                  ),
                ];
              }).flat().filter(Boolean)}
              {/* Summary row for all sessions */}
              <TableRow className="bg-slate-800">
                <TableCell></TableCell>
                <TableCell colSpan={4} className="text-xs font-bold text-white">{selectedCashier} — {sessions.length} session{sessions.length > 1 ? "s" : ""} TOTAL</TableCell>
                <TableCell className="text-xs text-right font-bold text-white"></TableCell>
                <TableCell className="text-xs text-center font-bold text-white">{sessions.reduce((s, x) => s + x.billCount, 0)}</TableCell>
                <TableCell className="text-xs text-right font-bold text-white tabular-nums">{formatCurrency(sessions.reduce((s, x) => s + x.totalAmount, 0))}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}


function SessionDrillDown({ data, isLoading }) {
  const [modeFilter, setModeFilter] = useState(""); // "" = all, or "Cash", "Card", etc.

  if (isLoading) return <div className="flex items-center gap-2 px-6 py-8 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading session details...</div>;
  if (!data) return null;

  const allBills = data.bills || [];
  const allTxns = data.transactions || [];
  const summary = data.summary || {};
  const session = data.session || {};

  // Use backend-provided summary for KPI cards (accounts for cash transfers)
  const modeSummary = {};
  for (const mode of MODES) { modeSummary[mode] = summary[mode] || 0; }
  modeSummary.total = summary.total || 0;

  // Build per-bill, per-mode tracking from transactions (for bill table display)
  const billTxnDetail = {};
  for (const t of allTxns) {
    const amt = t.amount || 0;
    const cat = t.category || "Others";
    if (t.billNo) {
      if (!billTxnDetail[t.billNo]) {
        billTxnDetail[t.billNo] = { byMode: {}, total: 0, primaryMode: cat, mode: t.mode || "" };
      }
      billTxnDetail[t.billNo].byMode[cat] = (billTxnDetail[t.billNo].byMode[cat] || 0) + amt;
      billTxnDetail[t.billNo].total += amt;
    }
  }
  // Set primary mode as the one with highest absolute amount
  for (const billNo in billTxnDetail) {
    const d = billTxnDetail[billNo];
    let maxCat = "", maxAmt = 0;
    for (const [cat, amt] of Object.entries(d.byMode)) {
      if (Math.abs(amt) > maxAmt) { maxAmt = Math.abs(amt); maxCat = cat; }
    }
    if (maxCat) d.primaryMode = maxCat;
  }

  // Filter bills: show bills that have ANY transaction in the filtered mode
  const filteredBills = modeFilter
    ? allBills.filter(b => (billTxnDetail[b.billNo]?.byMode[modeFilter] || 0) !== 0)
    : allBills;

  // Filtered total: sum only the filtered mode's amounts (not total bill amounts)
  const filteredBillTotal = modeFilter
    ? filteredBills.reduce((s, b) => s + (billTxnDetail[b.billNo]?.byMode[modeFilter] || 0), 0)
    : filteredBills.reduce((s, b) => s + (billTxnDetail[b.billNo]?.total ?? 0), 0);

  return (
    <div className="px-4 py-4 space-y-4">
      {/* Session Info Bar */}
      <div className="flex items-center gap-6 text-xs text-slate-600 bg-slate-100/80 rounded-lg px-4 py-2.5">
        <div className="flex items-center gap-1.5 font-semibold text-slate-800">
          <User className="w-3.5 h-3.5 text-indigo-500" /> {session.cashierName}
        </div>
        <div className="flex items-center gap-1.5">
          <Clock className="w-3 h-3 text-slate-400" /> {session.startTime} &rarr; {session.endTime || "In progress"}
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${STATUS_BADGE[session.status] || "bg-slate-100 text-slate-600"}`}>{session.status}</span>
        <div className="ml-auto flex items-center gap-4">
          <span className="text-slate-500">Opening: <span className="font-semibold text-slate-700">{formatCurrency(session.openingCash)}</span></span>
          <span className="text-slate-500">Ending: <span className="font-semibold text-slate-700">{formatCurrency(session.endingCash)}</span></span>
        </div>
      </div>

      {/* Payment Mode Summary Cards — clickable for drill-down */}
      <div className="grid grid-cols-8 gap-2">
        {MODES.map(mode => {
          const Icon = MODE_ICONS[mode];
          const style = MODE_STYLES[mode];
          const val = modeSummary[mode] || 0;
          const isActive = modeFilter === mode;
          return (
            <button key={mode}
              onClick={() => setModeFilter(prev => prev === mode ? "" : mode)}
              data-testid={`mode-card-${mode.toLowerCase()}`}
              className={`${style.bg} border ${isActive ? `${style.border} ring-2 ${style.ring}` : style.border} rounded-lg px-3 py-2.5 text-center transition-all hover:scale-[1.02] cursor-pointer ${val === 0 ? "opacity-50" : ""}`}>
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <Icon className={`w-3.5 h-3.5 ${style.color}`} />
                <span className={`text-[10px] font-bold uppercase tracking-wider ${style.color}`}>{mode}</span>
              </div>
              <p className={`text-sm font-bold ${style.color}`}>{formatCurrency(val)}</p>
            </button>
          );
        })}
        <div className="bg-slate-800 rounded-lg px-3 py-2.5 text-center">
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <DollarSign className="w-3.5 h-3.5 text-white" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Total</span>
          </div>
          <p className="text-sm font-bold text-white">{formatCurrency(modeSummary.total)}</p>
        </div>
      </div>

      {/* Active Mode Filter Indicator + Payment Mode Dropdown */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5 text-slate-600">
            <Receipt className="w-3.5 h-3.5 text-indigo-500" />
            <span className="font-semibold">{filteredBills.length}</span> Bills
          </div>
          <div className="flex items-center gap-1.5 text-slate-600">
            Bill Total: <span className="font-semibold">{formatCurrency(filteredBillTotal)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {modeFilter && (
            <span className={`px-2 py-1 rounded-lg text-[10px] font-bold border ${MODE_STYLES[modeFilter].bg} ${MODE_STYLES[modeFilter].border} ${MODE_STYLES[modeFilter].color}`}>
              Filtered: {modeFilter}
            </span>
          )}
          <select value={modeFilter} onChange={e => setModeFilter(e.target.value)}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-slate-50 focus:bg-white"
            data-testid="session-mode-filter">
            <option value="">All Modes</option>
            {MODES.map(m => <option key={m} value={m}>{m} ({formatCurrency(modeSummary[m] || 0)})</option>)}
          </select>
          {modeFilter && (
            <button onClick={() => setModeFilter("")}
              className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Bills Table — unified with payment mode */}
      {filteredBills.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-100/80">
                <TableHead className="text-[10px] font-bold uppercase text-slate-500">Bill No</TableHead>
                <TableHead className="text-[10px] font-bold uppercase text-slate-500">Order ID</TableHead>
                <TableHead className="text-[10px] font-bold uppercase text-slate-500">Type</TableHead>
                <TableHead className="text-[10px] font-bold uppercase text-slate-500">Patient</TableHead>
                <TableHead className="text-[10px] font-bold uppercase text-slate-500">MRN</TableHead>
                <TableHead className="text-[10px] font-bold uppercase text-slate-500">Date / Time (IST)</TableHead>
                <TableHead className="text-[10px] font-bold uppercase text-slate-500">Scheme</TableHead>
                <TableHead className="text-[10px] font-bold uppercase text-slate-500">Pay Mode</TableHead>
                <TableHead className="text-[10px] font-bold uppercase text-slate-500 text-right">Paid Amount</TableHead>
                <TableHead className="text-[10px] font-bold uppercase text-slate-500 text-right">Amount</TableHead>
                <TableHead className="text-[10px] font-bold uppercase text-slate-500 text-right">Tax</TableHead>
                <TableHead className="text-[10px] font-bold uppercase text-slate-500 text-right">Discount</TableHead>
                <TableHead className="text-[10px] font-bold uppercase text-slate-500 text-right">Grand Total</TableHead>
                <TableHead className="text-[10px] font-bold uppercase text-slate-500 text-center">Status</TableHead>
                <TableHead className="text-[10px] font-bold uppercase text-slate-500">Billed By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBills.map((b, i) => {
                const detail = billTxnDetail[b.billNo];
                const cat = modeFilter || detail?.primaryMode;
                const paidAmt = modeFilter
                  ? (detail?.byMode[modeFilter] || 0)
                  : (detail?.total ?? 0);
                // Determine display order IDs: use mergedOrderIds for consolidated, else single orderId
                const displayOrderIds = (b.isMergedRoot && b.mergedOrderIds?.length > 0)
                  ? b.mergedOrderIds
                  : (b.orderId ? [b.orderId] : []);
                // Get all modes for this bill
                const allModes = detail ? Object.entries(detail.byMode).filter(([, v]) => v !== 0) : [];
                return (
                  <TableRow key={i} className="hover:bg-slate-50/50" data-testid={`session-bill-${i}`}>
                    <TableCell className="text-xs font-medium text-slate-800">
                      <div>{b.billNo}</div>
                      {b.isMergedRoot && <span className="text-[9px] font-semibold text-indigo-500 bg-indigo-50 px-1 py-0.5 rounded mt-0.5 inline-block">Consolidated</span>}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-slate-600">
                      {displayOrderIds.length > 0 ? (
                        <div className="space-y-0.5">
                          {displayOrderIds.map((oid, idx) => (
                            <div key={idx} className="truncate max-w-[140px]" title={oid}>{oid}</div>
                          ))}
                        </div>
                      ) : "-"}
                    </TableCell>
                    <TableCell><span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600">{b.billTypeLabel}</span></TableCell>
                    <TableCell className="text-xs text-slate-700">{b.patientName}</TableCell>
                    <TableCell className="text-xs text-slate-500">{b.MRN}</TableCell>
                    <TableCell className="text-xs text-slate-600">{b.createdAt}</TableCell>
                    <TableCell className="text-xs text-slate-500">{b.schemeName || "-"}</TableCell>
                    <TableCell>
                      {modeFilter ? (
                        cat ? (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                            style={{ backgroundColor: (MODE_COLORS[cat] || "#94a3b8") + "18", color: MODE_COLORS[cat] || "#64748b" }}>
                            {cat}
                          </span>
                        ) : <span className="text-xs text-slate-400">-</span>
                      ) : allModes.length > 1 ? (
                        <div className="flex flex-wrap gap-0.5">
                          {allModes.map(([mode, amt]) => (
                            <span key={mode} className="px-1.5 py-0.5 rounded text-[9px] font-bold whitespace-nowrap"
                              style={{ backgroundColor: (MODE_COLORS[mode] || "#94a3b8") + "18", color: MODE_COLORS[mode] || "#64748b" }}>
                              {mode} {formatCurrency(amt)}
                            </span>
                          ))}
                        </div>
                      ) : cat ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                          style={{ backgroundColor: (MODE_COLORS[cat] || "#94a3b8") + "18", color: MODE_COLORS[cat] || "#64748b" }}>
                          {cat}
                        </span>
                      ) : <span className="text-xs text-slate-400">-</span>}
                    </TableCell>
                    <TableCell className="text-xs text-right tabular-nums font-semibold text-indigo-700">{formatCurrency(paidAmt)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{formatCurrency(b.totalAmount)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{formatCurrency(b.totalTax)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{formatCurrency(b.totalDiscount)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums font-semibold">{formatCurrency(b.grandTotal)}</TableCell>
                    <TableCell className="text-center">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${PAY_BADGE[b.paymentStatus] || "bg-slate-100 text-slate-600"}`}>{b.paymentStatus}</span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{b.billedBy}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow className="bg-slate-800">
                <TableCell colSpan={8} className="text-xs font-bold text-white">
                  {modeFilter ? `${modeFilter.toUpperCase()} BILLS (${filteredBills.length})` : `SESSION TOTAL (${filteredBills.length} bills)`}
                </TableCell>
                <TableCell className="text-xs text-right font-bold text-white tabular-nums">{formatCurrency(filteredBillTotal)}</TableCell>
                <TableCell className="text-xs text-right font-bold text-white tabular-nums">{formatCurrency(filteredBills.reduce((s, b) => s + (b.totalAmount || 0), 0))}</TableCell>
                <TableCell className="text-xs text-right font-bold text-white tabular-nums">{formatCurrency(filteredBills.reduce((s, b) => s + (b.totalTax || 0), 0))}</TableCell>
                <TableCell className="text-xs text-right font-bold text-white tabular-nums">{formatCurrency(filteredBills.reduce((s, b) => s + (b.totalDiscount || 0), 0))}</TableCell>
                <TableCell className="text-xs text-right font-bold text-white tabular-nums">{formatCurrency(filteredBills.reduce((s, b) => s + (b.grandTotal || 0), 0))}</TableCell>
                <TableCell colSpan={2}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
